import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import IgvMount from './IgvMount'
import type { HarnessConfig } from './harnessConfig'
import {
  decodeHarnessConfigFromHash,
  encodeHarnessConfigToHash,
  HARNESS_CONFIG_EXAMPLE,
  parseHarnessConfig,
  stringifyHarnessConfig,
  TRACK_SOFT_LIMIT,
} from './harnessConfig'

const PLACEHOLDER_JSON = stringifyHarnessConfig(HARNESS_CONFIG_EXAMPLE)

/**
 * IGV multi-track test harness for GEN-2611 / GEN-2880.
 *
 * Lets a developer paste a JSON config of up to 6 (warning above) IGV tracks -
 * VCFs, BAM/CRAMs, etc. - sourced from S3 signed URLs and mount them against
 * a chosen reference genome and locus. Intended for performance measurement
 * and S3-prereq validation, not end-user use.
 *
 * The current paste-state is reflected in the URL hash so a particular test
 * configuration can be shared as a link.
 */
const HarnessPage: React.FC = () => {
  const [rawText, setRawText] = useState<string>(PLACEHOLDER_JSON)
  const [mountedConfig, setMountedConfig] = useState<HarnessConfig | null>(null)
  const [mountKey, setMountKey] = useState<number>(0)
  const [parseError, setParseError] = useState<string | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [hashStatus, setHashStatus] = useState<string | null>(null)

  // Hydrate from #config=... on first mount so shareable links work.
  useEffect(() => {
    const decoded = decodeHarnessConfigFromHash(window.location.hash)
    if (!decoded) return
    if (!decoded.ok) {
      setHashStatus(`Could not load config from URL hash: ${decoded.error}`)
      return
    }
    setRawText(stringifyHarnessConfig(decoded.config))
    setMountedConfig(decoded.config)
    setWarnings(decoded.warnings)
    setMountKey((k) => k + 1)
    setHashStatus(`Loaded config from URL hash (${decoded.config.tracks.length} tracks).`)
  }, [])

  const handleLoad = useCallback(() => {
    const result = parseHarnessConfig(rawText)
    if (!result.ok) {
      setParseError(result.error)
      setWarnings([])
      return
    }
    setParseError(null)
    setWarnings(result.warnings)
    setMountedConfig(result.config)
    setMountKey((k) => k + 1)
  }, [rawText])

  const handleCopyLink = useCallback(async () => {
    const result = parseHarnessConfig(rawText)
    if (!result.ok) {
      setParseError(result.error)
      return
    }
    const hash = encodeHarnessConfigToHash(result.config)
    const url = `${window.location.origin}${window.location.pathname}${hash}`
    window.history.replaceState(null, '', url)
    try {
      await navigator.clipboard.writeText(url)
      setHashStatus('Shareable link copied to clipboard.')
    } catch {
      setHashStatus('Link is in the address bar (clipboard not available).')
    }
  }, [rawText])

  const handleReset = useCallback(() => {
    setRawText(PLACEHOLDER_JSON)
    setMountedConfig(null)
    setParseError(null)
    setWarnings([])
    setHashStatus(null)
    if (window.location.hash) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    }
  }, [])

  const trackSummary = useMemo(() => {
    if (!mountedConfig) return null
    return mountedConfig.tracks.map((t, i) => `${i + 1}. ${t.name} [${t.format}]`).join('\n')
  }, [mountedConfig])

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">IGV multi-track test harness</h1>
        <p className="text-text-subtle text-sm">
          Paste an IGV config below and click Load to mount up to {TRACK_SOFT_LIMIT} tracks (VCF / BAM / CRAM) served
          from S3 signed URLs. For GEN-2611 performance measurement only -{' '}
          <Link to="/home" className="text-brand-700 underline">
            back to home
          </Link>
          .
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Harness config (JSON)</span>
            <textarea
              data-testid="harness-config-input"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={20}
              spellCheck={false}
              className="w-full rounded border border-gray-300 p-2 font-mono text-xs"
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleLoad}
              data-testid="harness-load"
              className="bg-brand-600 hover:bg-brand-700 rounded px-4 py-2 text-sm font-medium text-white"
            >
              Load
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
            >
              Copy shareable link
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
            >
              Reset
            </button>
          </div>

          {parseError && (
            <p
              role="alert"
              data-testid="harness-parse-error"
              className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {parseError}
            </p>
          )}
          {warnings.length > 0 && (
            <ul
              data-testid="harness-warnings"
              className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
            >
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          {hashStatus && (
            <p data-testid="harness-hash-status" className="text-text-subtle text-xs">
              {hashStatus}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <h2 className="text-text-subtle text-sm font-semibold tracking-wide uppercase">Parsed</h2>
          {mountedConfig ? (
            <dl className="space-y-1 text-sm">
              <div>
                <dt className="inline font-medium">Genome:</dt>{' '}
                <dd className="inline">{mountedConfig.genome ?? 'hg38 (default)'}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Locus:</dt>{' '}
                <dd className="inline">{mountedConfig.locus ?? '(none - whole genome)'}</dd>
              </div>
              <div>
                <dt className="font-medium">Tracks ({mountedConfig.tracks.length}):</dt>
                <dd>
                  <pre className="overflow-x-auto rounded bg-gray-50 p-2 text-xs">{trackSummary}</pre>
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-text-subtle text-sm">Paste a config and click Load.</p>
          )}
        </div>
      </div>

      {mountedConfig && (
        <IgvMount
          data-testid="harness-igv"
          genome={mountedConfig.genome}
          locus={mountedConfig.locus}
          tracks={mountedConfig.tracks}
          reloadKey={mountKey}
        />
      )}
    </section>
  )
}

export default HarnessPage
